/**
 * Text direction handling for right-to-left languages (Arabic, Hebrew, ...).
 *
 * Three things have to change when an RTL language is picked:
 *   1. dir/lang on <html> — makes the browser run the bidi algorithm, flip flex
 *      rows and align text to the right;
 *   2. the stylesheet — the layout is written with physical properties in some
 *      360 places, so the build emits an rtlcss-mirrored twin of every
 *      stylesheet (see scripts/build-rtl-css.js) and we swap the hrefs here;
 *   3. the webfont — Kode Mono and Tomorrow carry no Arabic glyphs, so an
 *      Arabic face is pulled in and sits at the end of the font stacks.
 *
 * The chosen direction is mirrored into localStorage so the very first paint
 * after a reload is already correct, before /languages has answered.
 */

const STORAGE_KEY = 'direction';
const FONT_LINK_ID = 'rtl-webfont';
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@100..900&display=swap';

function loadRtlWebfont() {
  if (document.getElementById(FONT_LINK_ID)) return;

  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = FONT_HREF;
  document.head.appendChild(link);
}

/**
 * Point every same-origin stylesheet at its mirrored twin, or back again.
 * External stylesheets (Google Fonts, the CDN ones) are left alone. If a twin
 * is missing — the dev server serves styles from <style> tags and never emits
 * them — the href is put back so the page does not end up unstyled.
 */
function swapStylesheets(isRtl) {
  const links = document.querySelectorAll('link[rel="stylesheet"]');

  links.forEach((link) => {
    const href = link.getAttribute('href');
    if (!href || /^(https?:)?\/\//i.test(href)) return;

    const isMirrored = /\.rtl\.css(\?|$)/.test(href);
    if (isRtl === isMirrored) return;

    const next = isRtl
      ? href.replace(/\.css(\?|$)/, '.rtl.css$1')
      : href.replace(/\.rtl\.css(\?|$)/, '.css$1');

    link.onerror = () => {
      link.onerror = null;
      link.setAttribute('href', href);
    };
    link.setAttribute('href', next);
  });
}

/** Direction remembered from the previous visit, "ltr" until we know better. */
export function storedDirection() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'rtl' ? 'rtl' : 'ltr';
  } catch (e) {
    return 'ltr';
  }
}

export function applyDirection(direction, languageCode) {
  const isRtl = direction === 'rtl';
  const html = document.documentElement;

  html.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
  if (languageCode) {
    html.setAttribute('lang', String(languageCode).toLowerCase());
  }

  swapStylesheets(isRtl);
  if (isRtl) loadRtlWebfont();

  try {
    localStorage.setItem(STORAGE_KEY, isRtl ? 'rtl' : 'ltr');
  } catch (e) {
    // private mode — the direction still applies for this page view
  }
}
